import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import App from './App';
import './index.css';
import ErrorBoundary from './components/ErrorBoundary';
import { QueryProvider } from './providers/QueryProvider';
import { AuthProvider } from './providers/AuthProvider';
import { ToastProvider } from './components/ToastProvider';
import { ProtectedRoute } from './components/ProtectedRoute';
import { initSentry } from './utils/sentry';
import { initWebVitals } from './utils/webVitals';

// Initialize Sentry for error tracking
initSentry();

// Initialize Core Web Vitals monitoring
initWebVitals();

// Lazy load page components for better performance
const InspectionDemo = lazy(() => import('./pages/InspectionDemo'));
const PropertySelector = lazy(() => import('./pages/PropertySelector'));
const TemplateSelector = lazy(() => import('./pages/TemplateSelector'));
const InspectionPage = lazy(() => import('./pages/InspectionPage'));
const CustomerDashboard = lazy(() => import('./pages/CustomerDashboard'));
const InspectionDetailsView = lazy(
  () => import('./pages/InspectionDetailsView')
);
const PropertyManagement = lazy(() => import('./pages/PropertyManagement'));
const CustomerManagement = lazy(() => import('./pages/CustomerManagement'));
const AdminInspectionList = lazy(() => import('./pages/AdminInspectionList'));
const InspectionEditPage = lazy(() => import('./pages/InspectionEditPage'));
const TemplateManagement = lazy(() => import('./pages/TemplateManagement'));
const SchedulingPage = lazy(() => import('./pages/SchedulingPage'));
const ServiceManagement = lazy(() => import('./pages/ServiceManagement'));
const AdHocServiceManagement = lazy(
  () => import('./pages/AdHocServiceManagement')
);
const AdHocVisitCreate = lazy(() => import('./pages/AdHocVisitCreate'));
const AdHocInspectionPage = lazy(() => import('./pages/AdHocInspectionPage'));
const InvoiceManagement = lazy(() => import('./pages/InvoiceManagement'));
const CustomerInvoiceView = lazy(() => import('./pages/CustomerInvoiceView'));
const StudioPage = lazy(() => import('./pages/StudioPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const HomeWatchServicePage = lazy(() => import('./pages/HomeWatchServicePage'));
const ConciergeServicePage = lazy(() => import('./pages/ConciergeServicePage'));

// Loading component
// eslint-disable-next-line react-refresh/only-export-components
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-900 mx-auto"></div>
      <p className="mt-4 text-stone-600">Loading...</p>
    </div>
  </div>
);

// 404 Not Found component
// eslint-disable-next-line react-refresh/only-export-components
const NotFound = () => (
  <div className="flex items-center justify-center min-h-screen bg-stone-50">
    <div className="text-center px-4">
      <h1 className="text-6xl font-bold text-teal-900 mb-4">404</h1>
      <p className="text-xl text-stone-600 mb-8">
        The page you&apos;re looking for doesn&apos;t exist.
      </p>
      <a
        href="/"
        className="inline-block bg-teal-900 text-white px-6 py-3 rounded-lg hover:bg-teal-800 transition-colors"
      >
        Go Home
      </a>
    </div>
  </div>
);

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Could not find root element to mount to');
}

const root = ReactDOM.createRoot(rootElement);
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <AuthProvider>
        <QueryProvider>
          <ToastProvider>
            <BrowserRouter>
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
                  <Route path="/" element={<App />} />
                  <Route path="/inspection-demo" element={<InspectionDemo />} />

                  {/* Service Pages */}
                  <Route
                    path="/services/home-watch"
                    element={<HomeWatchServicePage />}
                  />
                  <Route
                    path="/services/concierge"
                    element={<ConciergeServicePage />}
                  />

                  {/* Blog Routes */}
                  <Route path="/blog" element={<BlogPage />} />
                  <Route path="/blog/:slug" element={<BlogPostPage />} />

                  {/* About Page */}
                  <Route path="/about" element={<AboutPage />} />

                  {/* FAQ Page */}
                  <Route path="/faq" element={<FAQPage />} />

                  {/* Admin Routes */}
                  <Route
                    path="/manage-properties"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <PropertyManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/manage-customers"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <CustomerManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/inspections"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <AdminInspectionList />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/templates"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <TemplateManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/services"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <ServiceManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/ad-hoc-services"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <AdHocServiceManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/ad-hoc-visit/create"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <AdHocVisitCreate />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/ad-hoc-inspection/:id"
                    element={
                      <ProtectedRoute requiredRole="inspector">
                        <AdHocInspectionPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/invoices"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <InvoiceManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/schedule"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <SchedulingPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/inspections/:id/edit"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <InspectionEditPage />
                      </ProtectedRoute>
                    }
                  />
                  {/* Sanity Studio Route */}
                  <Route
                    path="/studio/*"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <StudioPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Inspector Routes */}
                  <Route
                    path="/start-inspection"
                    element={
                      <ProtectedRoute requiredRole="inspector">
                        <PropertySelector />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/select-template"
                    element={
                      <ProtectedRoute requiredRole="inspector">
                        <TemplateSelector />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/inspection"
                    element={
                      <ProtectedRoute requiredRole="inspector">
                        <InspectionPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Customer Routes */}
                  <Route
                    path="/customer-dashboard"
                    element={
                      <ProtectedRoute>
                        <CustomerDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/inspection-details/:inspectionId"
                    element={
                      <ProtectedRoute>
                        <InspectionDetailsView />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/my-invoices"
                    element={
                      <ProtectedRoute>
                        <CustomerInvoiceView />
                      </ProtectedRoute>
                    }
                  />

                  {/* 404 Catch-All */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </ToastProvider>
        </QueryProvider>
      </AuthProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
